import HttpClient from "./httpClient";
import { API_URLS } from "../utils/apiUrls";
import { store } from "../store";

const PSA_METADATA_CACHE_TTL_MS = 5 * 60 * 1000;
const psaMetadataReads = new Map<string, { expiresAt: number; value?: any; pending?: Promise<any> }>();

function getCachedPsaMetadata<T>(key: string, load: () => Promise<T>): Promise<T> {
  const cached = psaMetadataReads.get(key);
  if (cached?.value !== undefined && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value as T);
  }
  if (cached?.pending) return cached.pending as Promise<T>;
  const pending = load()
    .then((value) => {
      psaMetadataReads.set(key, { value, expiresAt: Date.now() + PSA_METADATA_CACHE_TTL_MS });
      return value;
    })
    .catch((error) => {
      psaMetadataReads.delete(key);
      throw error;
    });
  psaMetadataReads.set(key, { pending, expiresAt: 0 });
  return pending;
}

export function clearPsaMetadataReadCache() {
  psaMetadataReads.clear();
}

interface TicketFilters {
  status?: string[];
  customer?: string[];
  priority?: string;
  assignedTo?: string;
  requester?: string;
  startDate?: string;
  endDate?: string;
  groupName?: string;
  categoryName?: string[];
  boardId?: number; // Add boardId for NinjaOne
  includeAllStatuses?: boolean;
  refreshToken?: string;
}

export interface ResolvedTicket {
  ticketId: string;
  displayId: string;
  title: string;
  /** False when the PSA was not asked (PSAs other than Autotask and Halo). */
  verified: boolean;
}

class SupervisorApi extends HttpClient {
  protected url;

  constructor() {
    super();
    this.url = API_URLS;
  }

  // Helper to convert URLSearchParams to object, preserving arrays for duplicate keys
  private convertParamsToObject(params: URLSearchParams): Record<string, string | string[]> {
    const queryParams: Record<string, string | string[]> = {};
    const seenKeys = new Set<string>();
    params.forEach((value, key) => {
      if (seenKeys.has(key)) {
        // Key already exists, convert to array
        const existing = queryParams[key];
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          queryParams[key] = [existing as string, value];
        }
      } else {
        queryParams[key] = value;
        seenKeys.add(key);
      }
    });
    return queryParams;
  }

  public getSuperopsTickets = (
    page: number,
    pageSize: number,
    filters?: TicketFilters,
    sort?: { attribute: string; order: "ASC" | "DESC" }[],
    onUpdate?: (value: any) => void
  ) => {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    const state = store?.getState();
    const userEmail = state?.auth?.user?.email;
    if (userEmail) {
      params.append("email", userEmail);
    }

    if (filters) {
      if (filters.groupName) params.append("groupName", filters.groupName);
      if (filters.categoryName && Array.isArray(filters.categoryName)) {
        filters.categoryName.forEach((c) => params.append("categoryName", c));
      }
      if (filters.status && Array.isArray(filters.status)) {
        filters.status.forEach((s) => params.append("status", s));
      }
      if (filters.customer && Array.isArray(filters.customer)) {
        filters.customer.forEach((c) => params.append("customer", c));
      }
      if (filters.includeAllStatuses) {
        params.append("includeAllStatuses", "true");
      }
      if (filters.refreshToken) {
        params.append("refreshToken", filters.refreshToken);
      }
      if (filters.assignedTo) {
        params.append("assignedTo", filters.assignedTo);
      }
    }

    if (sort && sort.length) {
      params.append("sort", `${sort[0].attribute},${sort[0].order}`);
    }

    const queryParams = this.convertParamsToObject(params);
    return this.getRevalidating(this.url.GET_TICKETS, queryParams, onUpdate);
  };

  public getNinjaoneTickets = (
    page: number,
    pageSize: number,
    filters?: TicketFilters,
    sort?: { attribute: string; order: "ASC" | "DESC" }[],
    onUpdate?: (value: any) => void
  ) => {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    const state = store?.getState();
    const userEmail = state?.auth?.user?.email;
    if (userEmail) {
      params.append("email", userEmail);
    }

    if (filters) {
      if (filters.groupName) {
        params.append("groupName", filters.groupName);
      }
      if (filters.categoryName && Array.isArray(filters.categoryName)) {
        filters.categoryName.forEach((c) => params.append("categoryName", c));
      }
      if (filters.status && Array.isArray(filters.status)) {
        filters.status.forEach((s) => params.append("status", s));
      }
      if (filters.customer && Array.isArray(filters.customer)) {
        filters.customer.forEach((c) => params.append("customer", c));
      }
      if (filters.boardId) {
        params.append("boardId", String(filters.boardId));
      }
      if (filters.includeAllStatuses) {
        params.append("includeAllStatuses", "true");
      }
      if (filters.refreshToken) {
        params.append("refreshToken", filters.refreshToken);
      }
      if (filters.assignedTo) {
        params.append("assignedTo", filters.assignedTo);
      }
    }

    if (sort && sort.length) {
      params.append("sort", `${sort[0].attribute},${sort[0].order}`);
    }

    const queryParams = this.convertParamsToObject(params);
    return this.getRevalidating(this.url.GET_TICKETS, queryParams, onUpdate);
  };

  public getAutotaskTickets = (
    page: number,
    pageSize: number,
    filters?: TicketFilters,
    sort?: { attribute: string; order: "ASC" | "DESC" }[],
    onUpdate?: (value: any) => void
  ) => {
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));

    const state = store?.getState();
    const userEmail = state?.auth?.user?.email;
    if (userEmail) {
      params.append("email", userEmail);
    }

    if (filters) {
      if (filters.groupName) {
        params.append("groupName", filters.groupName);
      }
      if (filters.categoryName && Array.isArray(filters.categoryName)) {
        filters.categoryName.forEach((c) => params.append("categoryName", c));
      }
      if (filters.status && Array.isArray(filters.status)) {
        filters.status.forEach((s) => params.append("status", s));
      }
      if (filters.customer && Array.isArray(filters.customer)) {
        filters.customer.forEach((c) => params.append("customer", c));
      }
      if (filters.includeAllStatuses) {
        params.append("includeAllStatuses", "true");
      }
      if (filters.refreshToken) {
        params.append("refreshToken", filters.refreshToken);
      }
      if (filters.assignedTo) {
        params.append("assignedTo", filters.assignedTo);
      }
    }

    if (sort && sort.length) {
      params.append("sort", `${sort[0].attribute},${sort[0].order}`);
    }

    const queryParams = this.convertParamsToObject(params);
    return this.getRevalidating(this.url.GET_TICKETS, queryParams, onUpdate);
  };

  public getNinjaoneBoardList = () => {
    return this.getCoalesced("/boards");
  };

  public getNinjaoneUsers = () => {
    return this.getCoalesced("/ninjaone-users");
  };

  public getTicketById = (
    ticketId: string,
    email?: string,
    options?: { includeSystemNotes?: boolean; forceRefresh?: boolean },
    onUpdate?: (value: any) => void
  ) => {
    const params: Record<string, string> = {};
    if (email) {
      params.email = email;
    }
    if (options?.includeSystemNotes) {
      params.includeSystemNotes = "true";
    }
    if (options?.forceRefresh) params.refreshToken = String(Date.now());
    return this.getRevalidating(`${this.url.GET_TICKETS}/${ticketId}`, params, onUpdate);
  };

  /** Ticket number, `#id`, integer or UUID -> the PSA's internal ticket ID (404 when none). */
  public resolveTicket = (query: string) =>
    this.get(`${this.url.GET_TICKETS}/resolve`, {}, { q: query }) as Promise<ResolvedTicket>;

  // DISABLED: pills service — re-enable with GET /pills + PillsBar (see server index.js)
  // public getPills = (customerId?: string | null) => {
  //   const params = customerId ? { customerId } : {};
  //   return this.getCoalesced(this.url.GET_PILLS, {}, params);
  // };
  public getPills = (_customerId?: string | null) => Promise.resolve([] as any[]);

  public getSuggestions = (query: string, customerId?: string, mspId?: string, ticketId?: string) => {
    const params: any = { query };
    if (customerId) params.customerId = customerId;
    if (mspId) params.mspId = mspId;
    if (ticketId) params.ticketId = ticketId;
    return this.getCoalesced(this.url.GET_SUGGESTIONS, {}, params);
  };

  public getTechnicianGroupList = (email?: string) => {
    const params: Record<string, string> = {};
    if (email) {
      params.email = email;
    }
    const identity = store?.getState()?.auth?.user?.email ?? "";
    return getCachedPsaMetadata(
      JSON.stringify(["technician-groups", identity, email || ""]),
      () => this.getCoalesced(this.url.GET_TECHNICIAN_GROUP_LIST, {}, params)
    );
  };

  public getAllTechnicianGroups = (platformName?: string) => {
    const params: Record<string, string> = { _ts: String(Date.now()) };
    if (platformName) {
      params.platformName = platformName;
    }
    return this.getCoalesced("/techgroup-list/all", {}, params);
  };

  public getTicketCategoryList = () => {
    const identity = store?.getState()?.auth?.user?.email ?? "";
    return getCachedPsaMetadata(
      JSON.stringify(["ticket-categories", identity]),
      () => this.getCoalesced("/ticket-categories")
    );
  };

  public getAllTicketCategories = (platformName?: string) => {
    const params: Record<string, string> = { _ts: String(Date.now()) };
    if (platformName) {
      params.platformName = platformName;
    }
    return this.getCoalesced("/ticket-categories/all", {}, params);
  };

  public getTechnicianList = () => this.getCoalesced(this.url.GET_TECHNICIAN_LIST);

  public getClientsList = () => this.getCoalesced(this.url.GET_CLIENTS_LIST);

  public getTicketStatusOptions = () => this.getCoalesced("/tickets/status-options");

  public submitFeedback = (feedbackData: {
    type: 'positive' | 'negative';
    ticketId: string;
    url: string;
    comment?: string;
    context?: any;
  }) => {
    return this.post('/feedback', {}, feedbackData);
  };

  public createAiThreadsForQueue = (data: {
    platformId: number;
    queueId: number;
    scope_type?: "queue" | "category";
  }) => {
    return this.post(this.url.CREATE_AI_THREADS_FOR_QUEUE, {}, data);
  };

  public getQueueAutoMode = (params: {
    platformId: number;
    queueId: number;
    scope_type?: "queue" | "category";
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append("platformId", String(params.platformId));
    queryParams.append("queueId", String(params.queueId));
    if (params.scope_type) {
      queryParams.append("scope_type", params.scope_type);
    }
    return this.getCoalesced(this.url.GET_QUEUE_AUTO_MODE, {}, Object.fromEntries(queryParams));
  };

  public updateQueueAutoMode = (data: {
    platformId: number;
    queueId: number;
    scope_type?: "queue" | "category";
    is_auto: boolean;
  }) => {
    return this.put(this.url.UPDATE_QUEUE_AUTO_MODE, {}, data);
  };

  public getAutoModeScopes = () => {
    return this.getCoalesced("/tickets/auto-mode-scopes", {}, { _ts: String(Date.now()) });
  };

  public updateAutoModeScopes = (data: {
    scopes: Array<{
      scope_type: "all" | "queue" | "category";
      queue_id: number;
      scope_name?: string;
    }>;
  }) => {
    return this.put("/tickets/auto-mode-scopes", {}, data);
  };

  public createTicket = (data: {
    title: string;
    description: string;
    companyID: string;
    status?: number;
    priority?: number;
    queueName?: string;
    categoryName?: string;
    assigneeId?: string;
  }) => {
    return this.post('/tickets/create', {}, data);
  };

  public getTicketCreateOptions = (
    scope?: string | { queueName?: string; categoryName?: string }
  ) => {
    const params: Record<string, string> = {};
    if (typeof scope === "string") {
      if (scope) {
        params.queueName = scope;
      }
    } else if (scope) {
      if (scope.queueName) {
        params.queueName = scope.queueName;
      }
      if (scope.categoryName) {
        params.categoryName = scope.categoryName;
      }
    }
    return this.getCoalesced('/tickets/create/options', {}, params);
  };

  public closeTicket = (ticketId: string) => {
    return this.patch(`/tickets/${ticketId}/close`);
  };

  public getTicketPropertyOptions = (ticketId: string, field: string, refresh = false) =>
    this.get(`/tickets/${encodeURIComponent(ticketId)}/property-options`, {}, refresh ? { field, refresh: "true" } : { field });

  public updateTicketProperty = (ticketId: string, field: string, value: string) =>
    this.patch(`/tickets/${encodeURIComponent(ticketId)}/properties`, {}, { field, value });

  public getTicketChatEnabled = (ticketId: string) => {
    return this.getCoalesced(`/tickets/${ticketId}/chat-enabled`);
  };

  public downloadTicketAttachment = (ticketId: string, attachmentId: string | number) =>
    this.getBlob(
      `/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(String(attachmentId))}/download`
    );

  public getTicketTimeEntries = (ticketId: string) =>
    this.getCoalesced(`/tickets/${encodeURIComponent(ticketId)}/time-entries`);
}

export default SupervisorApi;
